import { isValidEmail } from "./professionalContacts.rules";
import {
  LOAN_DETAIL_ROLE_LABELS,
  LOAN_DETAIL_REQUIRED_ROLES,
  QUOTE_TYPE_TO_LOAN_ROLE,
} from "./professionalContacts.types";

export const QUOTE_ROLE_TO_TYPE = Object.fromEntries(
  Object.entries(QUOTE_TYPE_TO_LOAN_ROLE).map(([type, role]) => [role, type])
);

export const BANK_ROLE_KEYS = ["bankSolicitor", "bankValuer", "monitoringSurveyor"];
// Receiver (v1.4.0) is wired up exactly like Administrator — same firm/individual/address
// picker, no lifecycle dates — per the "Receiver linked with Professional Details" request.
export const REPOSITORY_ROLE_KEYS = ["broker", "borrowerSolicitor", "administrator", "receiver"];

const pushRoleChecks = (checks, roleKeys, sectionId, draft, firmDetailsById) => {
  roleKeys.forEach((roleKey) => {
    const label = LOAN_DETAIL_ROLE_LABELS[roleKey];
    const isRequired = LOAN_DETAIL_REQUIRED_ROLES.includes(roleKey);
    const assignment = draft[roleKey] || {};

    if (isRequired) {
      const firmError = assignment.firmId ? null : `${label} is required`;
      checks.push({
        fieldId: `role-firm-${roleKey}`,
        domId: `role-firm-${roleKey}`,
        sectionId,
        label: `${label} firm`,
        filled: Boolean(assignment.firmId),
        error: firmError,
      });
    }

    if (assignment.firmId) {
      let contactError = null;
      if (!assignment.contactId) {
        contactError = `${label} active individual is required`;
      } else {
        const firmContacts = firmDetailsById[assignment.firmId]?.contacts || [];
        const contact = firmContacts.find((c) => c.id === assignment.contactId);
        if (contact?.email && !isValidEmail(contact.email)) {
          contactError = `${label} active individual has an invalid email`;
        }
      }
      checks.push({
        fieldId: `role-contact-${roleKey}`,
        domId: `role-contact-${roleKey}`,
        sectionId,
        label: `${label} active individual`,
        filled: Boolean(assignment.contactId),
        error: contactError,
      });

      const addressError = assignment.addressId ? null : `${label} address is required`;
      checks.push({
        fieldId: `role-address-${roleKey}`,
        domId: `role-address-${roleKey}`,
        sectionId,
        label: `${label} address`,
        filled: Boolean(assignment.addressId),
        error: addressError,
      });
    }
  });
};

const pushQuoteChecks = (checks, draft) => {
  const quoteCountByType = {};
  (draft.quotes || []).forEach((quote, index) => {
    const type = QUOTE_ROLE_TO_TYPE[quote.role] || quote.role || "Quote";
    quoteCountByType[type] = (quoteCountByType[type] || 0) + 1;
    const quoteNumber = quoteCountByType[type];

    const firmError = quote.firmId ? null : `Quote ${quoteNumber} (${type}) firm is required`;
    checks.push({
      fieldId: `quote-firm-${index}`,
      domId: `quote-firm-${type}-${index}`,
      sectionId: "quotes",
      label: `Quote ${quoteNumber} (${type}) firm`,
      filled: Boolean(quote.firmId),
      error: firmError,
    });

    const isValueFilled = Boolean(quote.value) && Number(quote.value) > 0;
    const valueError = isValueFilled ? null : `Quote ${quoteNumber} (${type}) value is required`;
    checks.push({
      fieldId: `quote-value-${index}`,
      domId: `quote-value-${type}-${index}`,
      sectionId: "quotes",
      label: `Quote ${quoteNumber} (${type}) value`,
      filled: isValueFilled,
      error: valueError,
    });
  });
};

/**
 * One entry per field `validateForm` used to check, in its original order (bank roles, then
 * other roles, then quotes). `domId` is the DOM id (differs from `fieldId` for quotes — a
 * quote's error key is not its DOM id, see the LLD §3.6 trap).
 */
export function buildProfessionalDetailsChecks(draft, firmDetailsById) {
  const checks = [];
  pushRoleChecks(checks, BANK_ROLE_KEYS, "bankParties", draft, firmDetailsById);
  pushRoleChecks(checks, REPOSITORY_ROLE_KEYS, "otherRoles", draft, firmDetailsById);
  pushQuoteChecks(checks, draft);
  return checks;
}

/**
 * `validateForm`'s return shape, derived from the checks, plus `firstInvalidDomId` for focus
 * (today's save-time focus on an invalid quote silently does nothing — this fixes it).
 */
export function validateProfessionalDetails(draft, firmDetailsById) {
  const checks = buildProfessionalDetailsChecks(draft, firmDetailsById);
  const errors = {};
  const problems = [];
  let firstInvalidSection = null;
  let firstInvalidField = null;
  let firstInvalidDomId = null;

  checks.forEach((check) => {
    if (!check.error) return;
    errors[check.fieldId] = check.error;
    problems.push(check.error);
    if (!firstInvalidField) {
      firstInvalidField = check.fieldId;
      firstInvalidSection = check.sectionId;
      firstInvalidDomId = check.domId;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    problems,
    firstInvalidSection,
    firstInvalidField,
    firstInvalidDomId,
  };
}
